'use client'

import React from 'react'
import HeaderAppRouter from '../Header'
import Footer from '../Footer'
import {
    Box,
    Button,
    Container,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material'
import useTranslation from '../../util/translations/useTranslation'
import Link from 'next/link'
import {ROUTE_CONTACT, ROUTE_COURSES, ROUTE_HOME, ROUTE_LANG_FITNESS, ROUTE_TRIAL} from '../../Routes'
import Head from 'next/head'

const NotFoundContent = () => {
    const {getT} = useTranslation()

    // Create a wrapper function for translations with fallbacks for missing keys
    const getTranslation = (key: string, fallback: string) => {
        const translation = getT(key)
        // If translation is missing (returns the key), use the fallback
        return translation === key ? fallback : translation
    }

    const recommendedPages = [
        {
            title: getTranslation('notFound.recommendedLinks.home', 'Startseite'),
            route: ROUTE_HOME,
        },
        {
            title: getTranslation('notFound.recommendedLinks.courses', 'Kurse'),
            route: ROUTE_COURSES,
        },
        {
            title: getTranslation('notFound.recommendedLinks.fitness', 'Fitness'),
            route: ROUTE_LANG_FITNESS,
        },
        {
            title: getTranslation('notFound.recommendedLinks.trial', 'Probetraining'),
            route: ROUTE_TRIAL,
        },
    ]

    return (
        <>
            <Head>
                <title>Seite nicht gefunden | First Class Performance</title>
                <meta name='robots' content='noindex, follow' />
            </Head>
            <HeaderAppRouter />
            <Container>
                <Box sx={{my: 8}}>
                    <Grid container spacing={4} alignItems='center' justifyContent='center'>
                        <Grid
                            size={{
                                xs: 12,
                                md: 8,
                            }}>
                            <Paper elevation={0} sx={{p: 4, backgroundColor: 'transparent'}}>
                                <Typography variant='h1' component='h1' gutterBottom>
                                    {getTranslation('notFound.title', 'Seite nicht gefunden')}
                                </Typography>
                                <Typography variant='body1' paragraph>
                                    {getTranslation('notFound.message', 'Die von Ihnen gesuchte Seite existiert leider nicht. Bitte überprüfen Sie die URL oder navigieren Sie über die folgenden Links.')}
                                </Typography>

                                <Typography variant='h6' sx={{mt: 4, mb: 2}}>
                                    {getTranslation('notFound.recommendedTitle', 'Empfohlene Seiten')}
                                </Typography>

                                <List>
                                    {recommendedPages.map((page, index) => (
                                        <ListItem
                                            key={index}
                                            component={Link}
                                            href={page.route}
                                            sx={{
                                                p: 1,
                                                borderRadius: 1,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{minWidth: '36px'}}>
                                                <span>→</span>
                                            </ListItemIcon>
                                            <ListItemText primary={page.title} />
                                        </ListItem>
                                    ))}
                                </List>

                                <Box mt={4} display='flex' justifyContent='flex-start' gap={2}>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        component={Link}
                                        href={ROUTE_HOME}
                                    >
                                        {getTranslation('notFound.backToHome', 'Zurück zur Startseite')}
                                    </Button>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        component={Link}
                                        href={ROUTE_CONTACT}
                                    >
                                        {getTranslation('notFound.contactUs', 'Kontaktieren Sie uns')}
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
            <Footer />
        </>
    );
}

export default NotFoundContent
